import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Badge, Button, Card, DashboardTabs, Field, Input, LoadingScreen, Textarea } from '../components/Ui';
import { PublicMenuThemeEditor } from '../components/PublicMenuThemeEditor';
import { useToast } from '../contexts/ToastContext';
import { useBusinessSettings, useResetBusinessTheme, useSaveBusinessSettings, useSaveBusinessTheme } from '../hooks/businessData';
import { uploadImage } from '../lib/upload';
import type { PublicMenuTheme } from '../../shared/public-menu-theme';
import { contrastRatio, resolvePublicTheme } from '../../shared/public-menu-theme';

interface Settings {
  id: string;
  subdomain: string;
  name: string;
  description: string;
  logoKey: string | null;
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  theme: PublicMenuTheme;
  localCurrency: string;
  currencySymbol: string;
  currencyDecimals: number | '';
  exchangeRate: number | '';
  whatsapp: string | null;
  timezone: string;
  inventoryLowStockThreshold: number;
  status: string;
}

function LogoPreview({ logoUrl, businessName }: { logoUrl: string | null; businessName: string }) {
  return logoUrl ? (
    <img src={logoUrl} alt="Logo" className="h-20 w-20 rounded-2xl border border-border object-cover shadow-sm" />
  ) : (
    <div className="grid h-20 w-20 place-items-center rounded-2xl border border-dashed border-border bg-surface-secondary text-2xl font-black text-muted shadow-sm">
      {(businessName.trim().charAt(0) || 'M').toUpperCase()}
    </div>
  );
}

export default function BusinessSettingsPage() {
  const { show } = useToast();
  const { data: settings, isLoading, isError, error } = useBusinessSettings();
  const saveSettingsMutation = useSaveBusinessSettings();
  const saveThemeMutation = useSaveBusinessTheme();
  const resetThemeMutation = useResetBusinessTheme();
  const [form, setForm] = useState<Settings | null>(null);
  const [themeDraft, setThemeDraft] = useState<PublicMenuTheme | null>(null);
  const [themeIssue, setThemeIssue] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState<'tema' | 'cobro'>('tema');

  useEffect(() => {
    if (!settings) return;
    setForm(settings);
    setThemeDraft(resolvePublicTheme(settings.theme, {
      primary: settings.primaryColor,
      secondary: settings.secondaryColor,
      accent: settings.accentColor,
    }));
  }, [settings]);

  useEffect(() => {
    if (isError) {
      show(error instanceof Error ? error.message : 'No se pudo cargar la configuración.', 'error');
    }
  }, [error, isError, show]);

  const resolvedPersistedTheme = useMemo(() => {
    if (!settings) return null;
    return resolvePublicTheme(settings.theme, {
      primary: settings.primaryColor,
      secondary: settings.secondaryColor,
      accent: settings.accentColor,
    });
  }, [settings]);

  const persistedSnapshot = useMemo(() => {
    if (!settings || !resolvedPersistedTheme) return null;
    return JSON.stringify({
      name: settings.name,
      description: settings.description,
      logoKey: settings.logoKey,
      logoUrl: settings.logoUrl,
      localCurrency: settings.localCurrency,
      currencySymbol: settings.currencySymbol,
      currencyDecimals: settings.currencyDecimals,
      exchangeRate: settings.exchangeRate,
      whatsapp: settings.whatsapp || '',
      timezone: settings.timezone,
      inventoryLowStockThreshold: settings.inventoryLowStockThreshold,
      theme: resolvedPersistedTheme,
    });
  }, [resolvedPersistedTheme, settings]);

  const currentSnapshot = useMemo(() => {
    if (!form || !themeDraft) return null;
    return JSON.stringify({
      name: form.name,
      description: form.description,
      logoKey: form.logoKey,
      logoUrl: form.logoUrl,
      localCurrency: form.localCurrency,
      currencySymbol: form.currencySymbol,
      currencyDecimals: form.currencyDecimals,
      exchangeRate: form.exchangeRate,
      whatsapp: form.whatsapp || '',
      timezone: form.timezone,
      inventoryLowStockThreshold: form.inventoryLowStockThreshold,
      theme: themeDraft,
    });
  }, [form, themeDraft]);

  if (isLoading || !form || !themeDraft || !resolvedPersistedTheme) return <LoadingScreen />;

  const hasUnsavedChanges = persistedSnapshot !== currentSnapshot;
  const blockingMessage = themeIssue || null;
  const saveStatus = busy
    ? 'Guardando cambios globales…'
    : blockingMessage
      ? 'La configuración tiene un bloqueo antes de guardar.'
      : hasUnsavedChanges
        ? 'Tienes cambios pendientes por guardar.'
        : 'Sincronizado.';

  const save = async (event: FormEvent) => {
    event.preventDefault();
    const primaryContrast = contrastRatio(themeDraft.colors.primary, themeDraft.colors.primaryForeground);
    if (primaryContrast < 4.5) {
      show('El botón principal necesita un contraste mínimo de 4.5:1.', 'error');
      return;
    }
    if (Object.values(themeDraft.colors).some((color) => !/^#[0-9A-F]{6}$/.test(color))) {
      show('Todos los colores deben usar el formato #RRGGBB.', 'error');
      return;
    }
    if (themeIssue) {
      show(themeIssue, 'error');
      return;
    }
    setBusy(true);
    try {
      await saveSettingsMutation.mutateAsync({
        payload: {
          name: form.name,
          description: form.description,
          logoKey: form.logoKey,
          primaryColor: themeDraft.colors.primary,
          secondaryColor: themeDraft.colors.secondary,
          accentColor: themeDraft.colors.accent,
          localCurrency: form.localCurrency.toUpperCase(),
          currencySymbol: form.currencySymbol,
          currencyDecimals: Number(form.currencyDecimals),
          exchangeRate: Number(form.exchangeRate),
          whatsapp: form.whatsapp || null,
          timezone: form.timezone,
          inventoryLowStockThreshold: form.inventoryLowStockThreshold,
        },
      });
      const savedTheme = await saveThemeMutation.mutateAsync({ theme: themeDraft });
      const resolvedTheme = resolvePublicTheme(savedTheme);
      setThemeDraft(resolvedTheme);
      setForm((current) => current ? {
        ...current,
        primaryColor: resolvedTheme.colors.primary,
        secondaryColor: resolvedTheme.colors.secondary,
        accentColor: resolvedTheme.colors.accent,
        theme: resolvedTheme,
      } : current);
      show('Configuración guardada.', 'success');
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo guardar.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const restoreTheme = async () => {
    if (!window.confirm('¿Restaurar el tema predeterminado? Se perderán los cambios de apariencia actuales.')) return;
    setRestoring(true);
    try {
      const restoredTheme = await resetThemeMutation.mutateAsync();
      const restored = resolvePublicTheme(restoredTheme);
      setThemeDraft(restored);
      setForm((current) => current ? {
        ...current,
        primaryColor: restored.colors.primary,
        secondaryColor: restored.colors.secondary,
        accentColor: restored.colors.accent,
        theme: restored,
      } : current);
      show('Tema predeterminado restaurado.', 'success');
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo restaurar el tema.', 'error');
    } finally {
      setRestoring(false);
    }
  };

  const chooseLogo = async (file?: File) => {
    if (!file) return;
    setUploading(true);
    try {
      const upload = await uploadImage(file, 'logo');
      setForm((current) => current ? { ...current, logoKey: upload.key, logoUrl: upload.publicUrl } : current);
      show('Logo subido; guarda la configuración para aplicarlo.', 'success');
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo subir el logo.', 'error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={save} className="grid gap-6">
      <DashboardTabs selectedKey={tab} onSelectionChange={(key) => setTab(key as 'tema' | 'cobro')}>
        <Card className="p-2">
          <DashboardTabs.ListContainer>
            <DashboardTabs.List aria-label="Configuración del negocio" className="flex flex-wrap items-center gap-2">
              <DashboardTabs.Tab id="tema">
                Tema
                <DashboardTabs.Indicator />
              </DashboardTabs.Tab>
              <DashboardTabs.Tab id="cobro">
                Cobro y contacto
                <DashboardTabs.Indicator />
              </DashboardTabs.Tab>
            </DashboardTabs.List>
          </DashboardTabs.ListContainer>
        </Card>

        {tab === 'tema' && <DashboardTabs.Panel id="tema" className="grid gap-6">
          <Card className="grid gap-5 p-6">
            <h2 className="text-xl font-black text-foreground">Marca y presentación</h2>
            <div className="grid gap-5 sm:grid-cols-[220px_minmax(0,1fr)]">
              <div className="flex flex-col items-start gap-4 rounded-2xl border border-border bg-surface-secondary/60 p-4">
                <LogoPreview logoUrl={form.logoUrl} businessName={form.name} />
                <label className="inline-flex cursor-pointer items-center justify-center rounded-xl border border-border bg-surface px-4 py-2 text-sm font-bold text-foreground transition hover:bg-surface-secondary">
                  {uploading ? 'Subiendo…' : 'Cambiar logo'}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/avif"
                    className="hidden"
                    disabled={uploading}
                    onChange={(event) => void chooseLogo(event.target.files?.[0])}
                  />
                </label>
              </div>

              <div className="grid gap-4">
                <Field label="Nombre">
                  <Input required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Nombre del negocio" />
                </Field>
                <Field label="Descripción pública">
                  <Textarea maxLength={1000} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} placeholder="Describe brevemente tu negocio" />
                </Field>
              </div>
            </div>
          </Card>

          <Card className="grid gap-5 p-6">
            <h2 className="text-xl font-black text-foreground">Tema</h2>
            <PublicMenuThemeEditor
              draft={themeDraft}
              onChange={setThemeDraft}
              onBlockingIssue={setThemeIssue}
              businessName={form.name}
              description={form.description}
              logoUrl={form.logoUrl}
              currencySymbol={form.currencySymbol}
            />
          </Card>
        </DashboardTabs.Panel>}

        {tab === 'cobro' && <DashboardTabs.Panel id="cobro" className="grid gap-6">
          <Card className="grid gap-5 p-6">
          <h2 className="text-xl font-black text-foreground">Cobro y contacto</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="WhatsApp">
              <Input pattern="\+?[0-9]{7,15}" value={form.whatsapp || ''} onChange={(event) => setForm({ ...form, whatsapp: event.target.value })} placeholder="+584121234567" />
            </Field>
            <Field label="Zona horaria">
              <Input required value={form.timezone} onChange={(event) => setForm({ ...form, timezone: event.target.value })} placeholder="America/Caracas" />
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Moneda local">
              <Input required maxLength={3} value={form.localCurrency} onChange={(event) => setForm({ ...form, localCurrency: event.target.value.toUpperCase() })} placeholder="VES" />
            </Field>
            <Field label="Símbolo">
              <Input required maxLength={8} value={form.currencySymbol} onChange={(event) => setForm({ ...form, currencySymbol: event.target.value })} placeholder="Bs" />
            </Field>
            <Field label="Decimales">
              <Input type="number" min="0" max="3" required value={form.currencyDecimals} onChange={(event) => setForm({ ...form, currencyDecimals: event.target.value === '' ? '' : Number(event.target.value) })} placeholder="2" />
            </Field>
          </div>

          <Field label="Tasa de cambio">
            <Input type="number" min="0.000001" step="any" required value={form.exchangeRate} onChange={(event) => setForm({ ...form, exchangeRate: event.target.value === '' ? '' : Number(event.target.value) })} placeholder="40.00" />
          </Field>
          </Card>
        </DashboardTabs.Panel>}
      </DashboardTabs>

      <div className="sticky bottom-4 z-20 flex items-center justify-between gap-3 rounded-2xl border border-border bg-surface/95 p-4 shadow-lg backdrop-blur">
        <div className="flex min-w-0 items-center gap-3">
          <Badge tone={blockingMessage ? 'amber' : hasUnsavedChanges ? 'blue' : 'green'}>
            {blockingMessage ? 'Bloqueado' : hasUnsavedChanges ? 'Cambios pendientes' : 'Sincronizado'}
          </Badge>
          <p className="min-w-0 truncate text-sm font-semibold text-muted">{saveStatus}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button type="button" variant="ghost" busy={restoring} onPress={() => void restoreTheme()}>Restaurar tema</Button>
          <Button type="submit" busy={busy} disabled={Boolean(blockingMessage)}>Guardar cambios</Button>
        </div>
      </div>
    </form>
  );
}
